import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// UI label -> backend enum value
const CATEGORY_OPTIONS = [
  { label: 'Inappropriate Language', value: 'Inappropriate Language' },
  { label: 'Rude/Unprofessional Behavior', value: 'Unprofessional Behavior' },
  { label: 'Lack of Cooperation', value: 'Non-Cooperation' },
  { label: 'Class Negligence', value: 'Class Negligence' }
];

const localNow = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };

export default function PeerFeedbackForm({ user, onClose }) {
  const headers = { 'x-user-id': String(user?._id || user?.id || '') };
  const [teachers, setTeachers] = useState([]);
  const [target, setTarget] = useState('');
  const [cats, setCats] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [when, setWhen] = useState(localNow());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/staff-feedback/teachers`, { headers })
      .then((r) => setTeachers(r.data || [])).catch(() => setMsg({ type: 'err', text: 'Could not load staff list' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (v) => setCats((c) => (c.includes(v) ? c.filter((x) => x !== v) : [...c, v]));

  const submit = async (e) => {
    e.preventDefault();
    if (!target) return setMsg({ type: 'err', text: 'Select a teacher' });
    if (!cats.length) return setMsg({ type: 'err', text: 'Select at least one category' });
    if (remarks.trim().length < 5) return setMsg({ type: 'err', text: 'Please write your remarks' });
    setBusy(true); setMsg({ type: '', text: '' });
    try {
      await axios.post(`${API_BASE_URL}/api/staff-feedback`, {
        targetTeacherId: target, categories: cats, remarks: remarks.trim(), incidentDate: new Date(when).toISOString()
      }, { headers });
      setMsg({ type: 'ok', text: '✅ Submitted privately. Thank you.' });
      setTimeout(onClose, 1400);
    } catch (err) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Submission failed' });
      setBusy(false);
    }
  };

  const input = 'w-full rounded-lg border border-slate-300 p-2 text-sm';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" role="dialog" aria-modal="true">
      <form onSubmit={submit} className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">Confidential Staff Concern Form</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-2xl leading-none text-slate-500">×</button>
        </div>
        <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-300 p-2 text-sm font-semibold text-emerald-800">
          🔒 Strictly Confidential: Your name will never be revealed to the teacher.
        </div>

        <label className="mt-4 block text-sm font-semibold">Target Teacher</label>
        <select className={input} value={target} onChange={(e) => setTarget(e.target.value)}>
          <option value="">-- Select teacher --</option>
          {teachers.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
        </select>

        <div className="mt-4 text-sm font-semibold">Category</div>
        <div className="mt-1 grid gap-1">
          {CATEGORY_OPTIONS.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={cats.includes(o.value)} onChange={() => toggle(o.value)} /> {o.label}
            </label>
          ))}
        </div>

        <label className="mt-4 block text-sm font-semibold">Incident Date &amp; Time</label>
        <input type="datetime-local" className={input} value={when} max={localNow()} onChange={(e) => setWhen(e.target.value)} />

        <label className="mt-4 block text-sm font-semibold">Remarks</label>
        <textarea className={input} rows={4} maxLength={3000} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Describe the incident factually" />

        {msg.text && <div className={`mt-3 text-sm font-semibold ${msg.type === 'ok' ? 'text-emerald-700' : 'text-red-600'}`}>{msg.text}</div>}
        <button type="submit" disabled={busy} className="mt-4 w-full rounded-xl bg-slate-900 py-3 font-bold text-white disabled:opacity-50">
          {busy ? 'Submitting…' : 'Submit Privately'}
        </button>
      </form>
    </div>
  );
}
